<script lang="ts">
  import SlideOver from '$components/reusable/SlideOver.svelte';
  import Tabs from '$components/reusable/Tabs.svelte';
  import LinkDetails from '$lib/pages/MyProfile/Edit/LinkDetails.svelte';
  import CollaborationDetails from '$lib/pages/MyProfile/Edit/CollaborationDetails.svelte';
  import PersonalDetails from '$lib/pages/MyProfile/Edit/PersonalDetails.svelte';
  import Button from '$components/reusable/Button.svelte';
  import { toastCustom } from '$utils/toasts';
  import { client } from '$lib/api/Client';
  import AcceleratorDetails from './AcceleratorDetails.svelte';
  import {
    completeProfileSchema,
    type UserAcceleratorProfile,
    type UserExtendedProfile,
    type UserProfile,
  } from '$shared/lib';
  import { superForm, type Infer } from 'sveltekit-superforms/client';
  import { zod } from 'sveltekit-superforms/adapters';
  import { useQueryClient } from '@tanstack/svelte-query';
  import { goto } from '$app/navigation';
  import { acceleratorProfileSchema } from '$shared/api';
  import { socialCardQueryKey } from '$lib/pages/Hackathon/SocialShareable.svelte';

  export let showEditProfileSlideover = false;
  export let profile: UserProfile;
  export let extendedProfile: UserExtendedProfile;
  export let acceleratorProfile: UserAcceleratorProfile | undefined;

  const queryClient = useQueryClient();
  const editProfileSuperForm = superForm<Infer<typeof completeProfileSchema>>(
    {
      avatarId: undefined,
      username: profile.username,
      displayName: profile.displayName,
      country: extendedProfile.country ?? '',
      city: extendedProfile.city ?? '',
      languages: extendedProfile.languages ?? [],
      about: extendedProfile.about ?? '',
      headline: extendedProfile.currentPosition ?? '',
      isUniversityStudent: extendedProfile.isUniversityStudent,
      githubHandle: extendedProfile.githubHandle ?? undefined,
      twitterHandle: extendedProfile.twitterHandle ?? undefined,
      telegramHandle: extendedProfile.telegramHandle ?? undefined,
      linkedinHandle: extendedProfile.linkedinHandle ?? undefined,
      interestedUseCases: extendedProfile.interestedUseCases,
      yourRoles: extendedProfile.jobRoles,
      yourSkills: extendedProfile.skills,
      lookingToBuild: extendedProfile.lookingToBuild ?? undefined,
      rolesLookingFor: extendedProfile.rolesLookingFor,
      lookingForCollab: extendedProfile?.lookingForCollab,
    },
    {
      SPA: true,
      dataType: 'json',
      validators: zod(completeProfileSchema),
      validationMethod: 'oninput',
      taintedMessage: false,
    },
  );

  const editAcceleratorProfileSuperForm = superForm<Infer<typeof acceleratorProfileSchema>>(
    {
      email: acceleratorProfile?.email ?? '',
      telegramUsername: acceleratorProfile?.telegramHandle ?? '',
      otherContactInfo: acceleratorProfile?.contactOther ?? undefined,
      profileAppendix: acceleratorProfile?.profileAppendix ?? undefined,
    },
    {
      SPA: true,
      dataType: 'json',
      validators: zod(acceleratorProfileSchema),
      validationMethod: 'oninput',
      taintedMessage: false,
    },
  );

  const {
    form: editProfileForm,
    options,
    validateForm: validateEditProfileForm,
    allErrors: editProfileAllErrors,
    reset: resetEditProfileForm,
  } = editProfileSuperForm;
  const {
    form: editAcceleratorProfileForm,
    validateForm: validateEditAcceleratorProfileForm,
    allErrors: editAcceleratorProfileAllErrors,
    reset: resetEditAcceleratorProfileForm,
  } = editAcceleratorProfileSuperForm;

  // Redefine this rather than using user.avatarUrl directly because we don't
  // actually want the avatar change to be reflected until the user clicks save
  let avatarUrl = profile.avatarUrl ?? '';
  let selectedTab = 0;
  let tabs = [
    {
      name: 'Personal',
    },
    {
      name: 'Collab',
    },
    {
      name: 'Social',
    },
    {
      name: 'Accelerator',
      textColor: 'orange-dark-10',
      hidden: !acceleratorProfile,
    },
  ];
  let showImageEditing: boolean;

  async function updateUser() {
    // Check if the edit profile form is valid
    const { valid: isEditProfileFormValid } = await validateEditProfileForm();
    if (!isEditProfileFormValid) {
      return;
    }

    // Check if the edit accelerator profile form is valid (if acceleratorProfile exists)
    if (acceleratorProfile) {
      const { valid: isEditAcceleratorProfileFormValid } =
        await validateEditAcceleratorProfileForm();
      if (!isEditAcceleratorProfileFormValid) {
        return;
      }
    }

    // Update the user
    try {
      const { user: updatedUser } = await client.users.update({
        avatarId: $editProfileForm.avatarId,
        username: $editProfileForm.username,
        displayName: $editProfileForm.displayName,
        country: $editProfileForm.country ?? '',
        city: $editProfileForm.city ?? '',
        languages: $editProfileForm.languages ?? [],
        about: $editProfileForm.about ?? '',
        headline: $editProfileForm.headline ?? '',
        isUniversityStudent: $editProfileForm.isUniversityStudent,
        githubHandle: $editProfileForm.githubHandle,
        twitterHandle: $editProfileForm.twitterHandle,
        telegramHandle: $editProfileForm.telegramHandle,
        linkedinHandle: $editProfileForm.linkedinHandle,
        interestedUseCases: $editProfileForm.interestedUseCases ?? [],
        yourRoles: $editProfileForm.yourRoles ?? [],
        yourSkills: $editProfileForm.yourSkills ?? [],
        lookingToBuild: $editProfileForm.lookingToBuild,
        rolesLookingFor: $editProfileForm.rolesLookingFor ?? [],
        lookingForCollab: $editProfileForm.lookingForCollab,
      });

      if (acceleratorProfile) {
        await client.users.updateAcceleratorProfile({
          email: $editAcceleratorProfileForm.email,
          telegramUsername: $editAcceleratorProfileForm.telegramUsername,
          otherContactInfo: $editAcceleratorProfileForm.otherContactInfo,
          profileAppendix: $editAcceleratorProfileForm.profileAppendix,
        });
      }

      showEditProfileSlideover = false;

      await queryClient.invalidateQueries({
        queryKey: ['profile', updatedUser.username],
      });
      await queryClient.invalidateQueries({
        queryKey: ['profileCompletion', updatedUser.username],
      });

      // Invalidate the social card query to ensure the social card is updated
      // with the new profile information
      await queryClient.invalidateQueries({
        queryKey: socialCardQueryKey(),
      });
      await goto(`/profiles/${updatedUser.username}`, { invalidateAll: true });
    } catch (error) {
      toastCustom({
        title: 'Something went wrong',
        description: 'An error occurred while updating your profile. Please try again.',
        negative: true,
        position: 'bottom-center',
      });
    }
  }

  // Reset the forms when the slideover is closed
  $: if (!showEditProfileSlideover) {
    resetEditProfileForm();
    resetEditAcceleratorProfileForm();
  }

  // Disable the save button if there are errors or the image is being edited
  $: disabled =
    $editProfileAllErrors.length > 0 ||
    $editAcceleratorProfileAllErrors.length > 0 ||
    showImageEditing;
</script>

<SlideOver title="Edit profile" bind:slideOverOpen={showEditProfileSlideover}>
  <div class="gap-y-lg flex h-full flex-col justify-between overflow-y-scroll pt-[3px]">
    <div class="w-full overflow-y-scroll">
      <Tabs bind:tabs bind:selectedTab />
    </div>
    <div class="relative flex-1">
      <div class="inset-0 overflow-y-scroll sm:absolute">
        {#if selectedTab == 0}
          <PersonalDetails bind:avatarUrl bind:showImageEditing superform={editProfileSuperForm} />
        {:else if selectedTab == 1}
          <CollaborationDetails superform={editProfileSuperForm} />
        {:else if selectedTab == 2}
          <LinkDetails superform={editProfileSuperForm} />
        {:else if selectedTab == 3 && acceleratorProfile}
          <AcceleratorDetails superform={editAcceleratorProfileSuperForm} />
        {/if}
      </div>
    </div>
    <div class="gap-x-lg flex items-center">
      <Button tertiary size="md" class="flex-1" on:click={() => (showEditProfileSlideover = false)}
        >Cancel</Button
      >
      <Button secondary size="md" class="flex-1" {disabled} on:click={updateUser}
        >Save Changes</Button
      >
    </div>
  </div>
</SlideOver>
